<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(135deg, #fff7c2 0%, #ffd6e0 100%)",
      fontFamily:
        props.block.fontFamily ||
        "'Fredoka', 'Baloo 2', 'Comic Sans MS', cursive, sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base * 1.1}px`,
    radius: `${props.block.cardRadius ?? 28}px`,
  };
});

const PALETTE = [
  "#fb7185",
  "#fbbf24",
  "#34d399",
  "#60a5fa",
  "#a78bfa",
  "#f472b6",
];
function color(i: number): string {
  return PALETTE[i % PALETTE.length];
}
</script>

<template>
  <div class="cd-kids" :style="containerStyle">
    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-kids__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        background: color(i),
        animationDelay: `${i * 220}ms`,
      }"
    >
      <span
        class="cd-kids__digit"
        :key="seg.value"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#ffffff',
        }"
      >
        {{ seg.value }}
      </span>
      <span
        class="cd-kids__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || 'rgba(255,255,255,0.95)',
        }"
      >
        {{ seg.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-kids {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 22px;
}
.cd-kids__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 4px solid rgba(0, 0, 0, 0.15);
  box-shadow:
    0 10px 0 rgba(0, 0, 0, 0.18),
    inset 0 -8px 0 rgba(0, 0, 0, 0.12),
    inset 0 4px 0 rgba(255, 255, 255, 0.35);
  animation:
    cd-kids-bob 2.4s ease-in-out infinite,
    cd-kids-pop 700ms cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  transition: transform 0.18s ease;
}
.cd-kids__cell:hover {
  transform: rotate(-3deg) scale(1.05);
}
@keyframes cd-kids-bob {
  0%,
  100% {
    transform: translateY(0) rotate(-1deg);
  }
  50% {
    transform: translateY(-10px) rotate(1deg);
  }
}
@keyframes cd-kids-pop {
  from {
    opacity: 0;
    transform: scale(0.4) rotate(-15deg);
  }
  to {
    opacity: 1;
    transform: scale(1) rotate(0);
  }
}
.cd-kids__digit {
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 0 rgba(0, 0, 0, 0.18);
  display: inline-block;
  animation: cd-kids-bounce var(--cd-tick, 600ms)
    cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes cd-kids-bounce {
  0% {
    transform: scale(0.3) translateY(-30px);
    opacity: 0;
  }
  60% {
    transform: scale(1.2) translateY(0);
    opacity: 1;
  }
  100% {
    transform: scale(1) translateY(0);
  }
}
.cd-kids__label {
  margin-top: 4px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
</style>
